import { useCallback, useEffect, useMemo, useState } from 'react'
import { Kicker } from './Kicker'
import { Button } from './Button'

const LS_KEY = 'babyos.kicks.log.v1'
const RETAIN_MS = 48 * 60 * 60 * 1000
const SPARK_WINDOW_MS = 60 * 60 * 1000
const SPARK_BINS = 6

type LogShape = Record<string, number[]>

function readLog(): LogShape {
  if (typeof localStorage === 'undefined') return {}
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const out: LogShape = {}
    const cutoff = Date.now() - RETAIN_MS
    for (const [id, arr] of Object.entries(parsed)) {
      if (!Array.isArray(arr)) continue
      const clean = arr.filter((n): n is number => typeof n === 'number' && n >= cutoff).sort((a, b) => a - b)
      if (clean.length) out[id] = clean
    }
    return out
  } catch {
    return {}
  }
}

function writeLog(log: LogShape) {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(log))
  } catch {
    /* quota — silently drop */
  }
}

function startOfLocalDay(now = Date.now()): number {
  const d = new Date(now)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

/**
 * Kicks Live card — Today surface for logging fetal movements without leaving
 * the main screen. On-device only (localStorage), same discretion posture as
 * the full KickCounterScreen. Rendered only for `person_type === 'unborn'`.
 *
 * Three signals in one tile: today's total, a 60-min cluster spark, and a big
 * +1 action. The spark reads as "kicks are clumping now" vs "quiet" without
 * pulling the user out to a dedicated screen.
 */
export function KicksCard({ personId, color }: { personId: string; color: string }) {
  const [log, setLog] = useState<LogShape>(() => readLog())
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(id)
  }, [])

  const kicks = useMemo(() => log[personId] ?? [], [log, personId])
  const todayStart = useMemo(() => startOfLocalDay(now), [now])
  const kicksToday = useMemo(() => kicks.filter((t) => t >= todayStart), [kicks, todayStart])
  const lastKickAt = kicks[kicks.length - 1] ?? null

  const spark = useMemo(() => {
    const windowStart = now - SPARK_WINDOW_MS
    const binSize = SPARK_WINDOW_MS / SPARK_BINS
    const bins = new Array<number>(SPARK_BINS).fill(0)
    for (const t of kicks) {
      if (t < windowStart) continue
      const idx = Math.min(SPARK_BINS - 1, Math.floor((t - windowStart) / binSize))
      bins[idx] = (bins[idx] ?? 0) + 1
    }
    const max = Math.max(1, ...bins)
    return { bins, max }
  }, [kicks, now])

  const addKick = useCallback(() => {
    const ts = Date.now()
    setLog((prev) => {
      const next: LogShape = { ...prev }
      const arr = next[personId] ? [...next[personId]!] : []
      arr.push(ts)
      next[personId] = arr
      writeLog(next)
      return next
    })
  }, [personId])

  const lastRelative = useMemo(() => {
    if (lastKickAt == null) return 'No kicks logged yet'
    const deltaMs = Math.max(0, now - lastKickAt)
    const min = Math.floor(deltaMs / 60_000)
    if (min < 1) return 'Just now'
    if (min < 60) return `${min}m ago`
    const hr = Math.floor(min / 60)
    return `${hr}h ${min % 60}m ago`
  }, [lastKickAt, now])

  return (
    <section aria-labelledby="kicks-card-heading">
      <Kicker color={color}>Kicks today</Kicker>
      <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle p-4">
        <div className="flex items-end gap-4">
          <div className="flex-1 min-w-0">
            <div id="kicks-card-heading" className="numeric-hero text-text-primary" style={{ fontSize: 'clamp(48px, 12vw, 72px)', color }}>
              {kicksToday.length}
            </div>
            <div className="mt-1 text-caption text-text-secondary">
              Last kick · {lastRelative}
            </div>
            <div className="mt-3 flex items-end gap-[3px] h-8" aria-label="Kicks in the last 60 minutes, split into 10-minute buckets" role="img">
              {spark.bins.map((count, i) => {
                const heightPct = (count / spark.max) * 100
                return (
                  <div
                    key={i}
                    className="flex-1 rounded-sm"
                    style={{
                      height: `${Math.max(6, heightPct)}%`,
                      background: count > 0 ? color : color + '33',
                      opacity: count > 0 ? 1 : 0.35,
                    }}
                    title={`${count} kick${count === 1 ? '' : 's'} · bin ${i + 1}/6`}
                  />
                )
              })}
            </div>
            <div className="mt-1 text-tag text-text-quiet">Last 60 min · 10-min bins</div>
          </div>
          <Button
            variant="primary"
            size="lg"
            onClick={addKick}
            aria-label="Log a kick"
            className="shrink-0"
          >
            +1 kick
          </Button>
        </div>
        <div className="mt-3 text-tag text-text-quiet">
          On this device only. Not sent to the server.
        </div>
      </div>
    </section>
  )
}
